import React, { useEffect, useMemo } from 'react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import translations from '../../translations/i18next';
import SpeechSynthesizer from './SpeechSynthesizer';

export default function SpeechController({ options, questionNumber, questionName, questionBody, stopSpeaking }) {
  const [open, setOpen] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState('all');
  const [isSpeaking, setIsSpeaking] = React.useState(true);
  const anchorRef = React.useRef(null);

  useEffect(() => {
    setSelectedIndex('all');
  }, [questionNumber]);

  useEffect(() => {
    if (stopSpeaking) setIsSpeaking(false);
  }, [stopSpeaking]);

  const allSpeechedText = useMemo(() => {
    return `${translations.t(
      'submissions.question'
    )} ${questionNumber} - ${questionName}. ${questionBody}.${translations.t('submissions.options')}: ${options
      .map((option) => option.value)
      .join(', ')}`;
  }, [questionNumber, questionName, questionBody, options]);

  const speechedText = useMemo(() => {
    if (selectedIndex === null) return '';
    if (selectedIndex === 'all') return allSpeechedText;
    if (selectedIndex === 'question') return questionBody;
    return `${options[selectedIndex].value}`;
  }, [selectedIndex, allSpeechedText, options, questionBody]);

  const getMenuItemLabel = (index) => {
    if (index === 'all') return translations.t('submissions.all');
    if (index === 'question') return translations.t('submissions.question');
    return `${translations.t('submissions.option')} ${options[index].id}`;
  };

  const handleMenuItemClick = (index) => {
    setSelectedIndex(index);
    setOpen(false);
    setIsSpeaking(true);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  return (
    <Grid container direction="column" alignItems="center" style={{ width: 100, alignSelf: 'center', marginTop: 8 }}>
      <Grid item xs={12} style={{ zIndex: 1000 }}>
        <ButtonGroup variant="outlined" ref={anchorRef} aria-label="split button">
          <Button style={{ padding: 0 }}>
            <SpeechSynthesizer text={speechedText} speak={isSpeaking} />
          </Button>
          <Button
            size="small"
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-label="select merge strategy"
            aria-haspopup="menu"
            onClick={handleToggle}
            style={{ padding: 0 }}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
        <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom'
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu">
                    <MenuItem onClick={() => handleMenuItemClick('all')} selected={selectedIndex === 'all'}>
                      {getMenuItemLabel('all')}
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick('question')} selected={selectedIndex === 'question'}>
                      {getMenuItemLabel('question')}
                    </MenuItem>
                    {options.map((option, index) => (
                      <MenuItem
                        key={option.id}
                        selected={index === selectedIndex}
                        onClick={() => handleMenuItemClick(index)}
                      >
                        {getMenuItemLabel(index)}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Grid>
    </Grid>
  );
}
